import { CATEGORIES, isCategoryKey, type CategoryKey } from "../categories";

/**
 * Value types (WALLIYO-0066): the named types a setting or rule value can have.
 *
 * Each is defined once and referred to by name, so `Theme` means the same thing
 * for a customer's theme and a staff member's console theme.
 *
 * - `SettingValue` is the data itself: `"dark"`, `10`, `["mon", "tue"]`.
 * - A `ValueType` describes it: `Theme` is an enum of system, light, dark.
 */

export type Unit = "km" | "GNF" | "minutes" | "hours" | "days";

export interface BooleanType {
  type: "boolean";
}

export interface NumberType {
  type: "number";
  unit?: Unit;
  min?: number;
  max?: number;
  /** Whole numbers only, e.g. an amount in GNF, which has no smaller unit. */
  integer?: boolean;
  /**
   * An amount of money. The value type names no currency: each stored value
   * carries its own (`setting_values.currency`), so one setting can hold a
   * value per currency and a new country needs no new type.
   */
  money?: boolean;
}

export interface StringType {
  type: "string";
  maxLength?: number;
}

/** A type defined by listing its allowed values. */
export interface EnumType<V extends string = string> {
  type: "enum";
  values: readonly V[];
}

export type ValueType = BooleanType | NumberType | StringType | EnumType;

/** Keeps an enum's values as literal types, so a default outside them is a compile error. */
function enumType<const V extends string>(values: readonly V[]): EnumType<V> {
  return { type: "enum", values };
}

/** Every trade, checked at runtime by the isCategoryKey guard. */
const TRADES: readonly CategoryKey[] = Object.keys(CATEGORIES).filter(isCategoryKey);

/** Every value type in Walliyo. Add a new one here before a setting uses it. */
export const VALUE_TYPES = {
  Boolean: { type: "boolean" },
  Theme: enumType(["system", "light", "dark"]),
  ColorVibe: enumType(["preferred", "ocean", "sunset", "berry", "slate", "gold", "teal", "rose"]),
  Language: enumType(["fr", "en", "de", "nl", "pt", "es", "it", "ar"]),
  Weekday: enumType(["mon", "tue", "wed", "thu", "fri", "sat", "sun"]),
  Trade: enumType(TRADES),
  Kilometres: { type: "number", unit: "km", min: 1 },
  ShortText: { type: "string", maxLength: 120 },
  /**
   * An amount of money in Guinean francs. Only the retired
   * REFUND_OWNER_APPROVAL_ABOVE_RULE uses it: new money values are `Money`.
   */
  GnfAmount: { type: "number", unit: "GNF", min: 0, integer: true },
  /** An amount of money in the currency stored with the value. */
  Money: { type: "number", min: 0, money: true },
  Minutes: { type: "number", unit: "minutes", min: 1, integer: true },
  Hours: { type: "number", unit: "hours", min: 1, integer: true },
  Days: { type: "number", unit: "days", min: 1, integer: true },
} satisfies Record<string, ValueType>;

export type ValueTypes = typeof VALUE_TYPES;
export type ValueTypeName = keyof ValueTypes;

/** The TypeScript type of one value of a given value type. */
export type ValueOf<N extends ValueTypeName> = ValueTypes[N] extends BooleanType
  ? boolean
  : ValueTypes[N] extends NumberType
    ? number
    : ValueTypes[N] extends EnumType<infer V>
      ? V
      : string;

/** One value, or a list of them when the setting holds several. */
export type ValueOrList<N extends ValueTypeName, L extends boolean> = L extends true
  ? readonly ValueOf<N>[]
  : ValueOf<N>;

/** Any setting's value: the data itself, e.g. `"dark"`, `10`, `["mon", "tue"]`. */
export type SettingValue = {
  [N in ValueTypeName]: ValueOf<N> | readonly ValueOf<N>[];
}[ValueTypeName];

/**
 * Whether one value fits its value type at runtime: the right kind of value,
 * within its bounds, and one of an enum's values. The database only sees a
 * typed column, so the server checks this before writing a setting or rule.
 */
export function valueFits(valueType: ValueType, value: unknown): boolean {
  switch (valueType.type) {
    case "boolean":
      return typeof value === "boolean";
    case "number":
      return (
        typeof value === "number" &&
        Number.isFinite(value) &&
        (valueType.min === undefined || value >= valueType.min) &&
        (valueType.max === undefined || value <= valueType.max) &&
        (valueType.integer !== true || Number.isInteger(value))
      );
    case "string":
      return typeof value === "string" && (valueType.maxLength === undefined || value.length <= valueType.maxLength);
    case "enum":
      return typeof value === "string" && valueType.values.some((allowed) => allowed === value);
  }
}
